// src/services/admin.user.service.js

import { apiClient } from "@/config/api.config";

/**
 * Service to handle all user-related API calls for admin
 */
class AdminUserService {
  /**
   * Get all users with pagination
   * @param {number} page - Page number
   * @param {number} limit - Items per page
   * @param {string} search - Search term
   * @returns {Promise} - Promise with user data
   */
  async getUsers(page = 1, limit = 10, search = "") {
    try {
      const response = await apiClient.get("/admin/users", {
        params: { page, limit, search },
      });

      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  /**
   * Get dashboard statistics for admin
   * @param {string} token - Auth token
   * @returns {Promise<Object>} - Dashboard stats data
   */
  async getDashboardStats(token) {
    try {
      const response = await apiClient.get("/admin/dashboard/stats", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  //admin profile
  async getAdminProfile(token) {
    return await apiClient.get("/admin/profile", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async updateAdminProfile(data, token) {
    return await apiClient.patch("/admin/editProfile", data, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      timeout: 10000,
    });
  }

  _handleError(error) {
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    return new Error(error.message || "Something went wrong");
  }
  /**
   * Get specific user details by ID
   * @param {string} userId - User ID
   * @returns {Promise} - Promise with user details
   */
  async getUserById(userId) {
    try {
      const response = await apiClient.get(`/admin/users/${userId}`);
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  async suspendUser(userId) {
    try {
      const response = await apiClient.put(`/admin/users/${userId}/suspend`);
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Update user status (active/inactive)
   * @param {string} userId - User ID
   * @param {string} status - New status
   * @returns {Promise} - Promise with updated user
   */
  async updateUserStatus(userId, status) {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/status`, {
        status,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Update user KYC verification status
   * @param {string} userId - User ID
   * @param {boolean} isVerified - Verification status
   * @returns {Promise} - Promise with updated user
   */
  async updateKycStatus(userId, isVerified) {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/kyc`, {
        isVerified,
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  //kyc request
  async getKycRequests(page = 1, limit = 10, status = "", token) {
    try {
      const response = await apiClient.get("/admin/kyc/requests", {
        params: {
          page,
          limit,
          status: status || undefined,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  /**
   * Download KYC document
   * @param {string} requestId - KYC request ID
   * @param {string} documentType - Document type (e.g., "panCard", "aadharFront", etc.)
   * @param {string} token - Bearer token for auth
   * @returns {Promise<Blob>} - Returns a Blob representing the file
   */
  async downloadKycDocument(requestId, documentType, token) {
    try {
      const response = await apiClient.get(
        `/auth/kyc/${requestId}/document/${documentType}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );
      return {
        blob: new Blob([response.data], {
          type: response.headers["content-type"],
        }),
        documentType,
        requestId,
      };
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Fetch all user watchlists (admin)
   * @param {number} page - Current page
   * @param {number} limit - Number of items per page
   * @param {string} token - Auth token
   * @returns {Promise<Object>} - Watchlists with pagination
   */
  async getAllUserWatchlists(page = 1, limit = 5, token) {
    try {
      const response = await apiClient.get("/products/watchlist/admin/all", {
        params: {
          page,
          limit,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Get wallets with pagination and filters
   * @param {Object} params - Query params including page, limit, filters, etc.
   * @returns {Promise} - Promise with wallets data
   */
  async getWallets(params = {}) {
    try {
      const response = await apiClient.get("/admin/wallets", {
        params,
      });

      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Fetch transactions with pagination and filters
   * @param {Object} params - Query parameters like page, limit, search, status, type, date range, amount range
   * @returns {Promise} - Promise with transactions data
   */
  async getTransactions(params = {}) {
    try {
      const response = await apiClient.get("/admin/alltrasactions", {
        params,
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Expires: "0",
        },
      });
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  /**
   * Reject a KYC request with a comment
   * @param {string} kycId - KYC ID to reject
   * @param {string} comment - Rejection comment
   * @param {string} token - Admin auth token
   * @returns {Promise<Object>} - Response from server
   */
  async rejectKycWithComment(kycId, comment, token) {
    try {
      const response = await apiClient.post(
        `/admin/kyc/${kycId}/verify`,
        {
          status: "rejected",
          comments: comment,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  /**
   * Verify a KYC request
   * @param {string} kycId - ID of the KYC request
   * @param {string} token - Auth token
   */
  async verifyKyc(kycId, token) {

    try {
      const response = await apiClient.post(
        `/admin/kyc/${kycId}/verify`,
        { status: "verified", comments: "" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }
  /**
   * Mark a KYC request as in-review
   * @param {string} kycId - ID of the KYC request
   * @param {string} token - Auth token
   */
  async markInReviewKyc(kycId, token) {
    try {
      const response = await apiClient.post(
        `/admin/kyc/${kycId}/mark-in-review`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Update user details
   * @param {string} userId - User ID
   * @param {Object} userData - User data to update
   * @returns {Promise} - Promise with updated user
   */
  async updateUser(userId, userData) {
    try {
      const response = await apiClient.patch(
        `/admin/users/${userId}/update`,
        userData
      );
      return response.data;
    } catch (error) {
      throw this._handleError(error);
    }
  }





  async createFilter(filterData) {
    try {
      const response = await apiClient.post('/admin/create', filterData);

      if (!response.success)

        return response;
    } catch (error) {
      throw error;
    }
  }

  // Get all filters (optional - for future use)
  async fetchAllFilters() {
    try {
      const response = await apiClient.get("/admin/all");

      // Handle different response structures
      if (response.data) {
        // If response has a success flag, return as is
        if (response.data.hasOwnProperty('success')) {
          return response.data;
        }
        // If response.data is directly the filters array
        else if (Array.isArray(response.data)) {
          return {
            success: true,
            data: response.data
          };
        }
        // If response.data is an object with filters property
        else if (response.data.filters) {
          return {
            success: true,
            data: response.data.filters
          };
        }
        // Default case
        else {
          return {
            success: true,
            data: response.data
          };
        }
      }

      return {
        success: false,
        message: "No data received from server"
      };
    } catch (error) {
      return {
        success: false,
        message: this._handleError(error)
      };
    }
  }


















  // Fixed API service methods for admin.user.service.js



  /**
   * Get all filters
   * @returns {Promise<Object>} - Response with filters data
   */


  /**
   * Update a filter
   * @param {string} filterId - ID of the filter to update
   * @param {Object} updateData - Data to update the filter with
   * @returns {Promise<Object>} - Response indicating success/failure
   */
  async updateFilter(filterId, updateData) {
    try {
      // Clean up the update data
      const cleanUpdateData = { ...updateData };

      // Ensure criteria is properly formatted
      if (cleanUpdateData.criteria) {
        Object.keys(cleanUpdateData.criteria).forEach(key => {
          if (!Array.isArray(cleanUpdateData.criteria[key])) {
            cleanUpdateData.criteria[key] = [cleanUpdateData.criteria[key]];
          }
          // Remove empty values
          cleanUpdateData.criteria[key] = cleanUpdateData.criteria[key].filter(
            value => value !== null && value !== undefined && value !== ""
          );
        });

        // Remove empty criteria keys
        Object.keys(cleanUpdateData.criteria).forEach(key => {
          if (cleanUpdateData.criteria[key].length === 0) {
            delete cleanUpdateData.criteria[key];
          }
        });
      }

      const response = await apiClient.put(
        `/admin/update/${filterId}`,
        cleanUpdateData
      );



      // Handle different response structures
      if (response.data) {
        if (response.data.hasOwnProperty('success')) {
          return response.data;
        } else {
          return {
            success: true,
            data: response.data
          };
        }
      }

      return {
        success: false,
        message: "No data received from server"
      };
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 400) {
        return {
          success: false,
          message: error.response.data?.message || "Invalid filter data provided"
        };
      } else if (error.response?.status === 404) {
        return {
          success: false,
          message: "Filter not found"
        };
      }

      return {
        success: false,
        message: this._handleError(error)
      };
    }
  }

  /**
   * Delete a filter
   * @param {string} filterId - ID of the filter to delete
   * @returns {Promise<Object>} - Response indicating success/failure
   */
  async deleteFilter(filterId) {
    try {
      // Validate filterId
      if (!filterId || typeof filterId !== 'string' || filterId.trim() === '') {
        return {
          success: false,
          message: "Invalid filter ID provided"
        };
      }



      // Use the correct DELETE endpoint and method
      const response = await apiClient.delete(
        `/admin/deletefilter/${filterId}`
      );



      return response.data;
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 400) {
        return {
          success: false,
          message: error.response.data?.message || "Invalid filter ID format"
        };
      } else if (error.response?.status === 404) {
        return {
          success: false,
          message: "Filter not found"
        };
      } else if (error.response?.status === 403) {
        return {
          success: false,
          message: "You don't have permission to delete filters"
        };
      }

      return {
        success: false,
        message: "Failed to delete filter due to a network error"
      };
    }
  }
  /**
   * Handle API errors consistently
   * @param {Error} error - The error object
   * @returns {string} - Formatted error message
   */
  _handleError(error) {
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || error.response.data?.error;
      if (message) {
        return message;
      }
      return `Server error: ${error.response.status} ${error.response.statusText}`;
    } else if (error.request) {
      // Request was made but no response received
      return "Network error: Unable to connect to server";
    } else {
      // Something else happened
      return error.message || "An unexpected error occurred";
    }
  }
}
/**
 * Handle API errors
 * @private
 * @param {Error} error - Error object
 * @returns {Error} - Processed error
 */




export const adminUserService = new AdminUserService();
